import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";

export default function CustomerPaymentHistory() {
  const navigate = useNavigate();
  const { tokenNo } = useParams();

  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  
  // Payment dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  
  // Menu states for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMode: "Cash",
    paymentStatus: "Completed",
    transactionDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  useEffect(() => {
    fetchCustomerData();
    fetchPaymentHistory();
  }, [tokenNo]);

  const fetchCustomerData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/customers/${tokenNo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomer(data.data);
      } else {
        toast.error("Failed to load customer details");
        navigate("/admin/customers");
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      toast.error("Failed to load customer details");
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/payments/customer/${tokenNo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setCurrentBalance(data.currentBalance || 0);
      } else {
        toast.error("Failed to load payment history");
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Failed to load payment history");
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      if (editingTransaction) {
        // Update existing transaction
        const response = await fetch(`${API_BASE_URL}/api/payments/transaction/${editingTransaction.TransactionId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(paymentForm.amount),
            paymentMode: paymentForm.paymentMode,
            paymentStatus: paymentForm.paymentStatus,
            transactionDate: paymentForm.transactionDate,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment updated successfully");
          
          // Trigger data refresh events
          setTimeout(() => {
            window.dispatchEvent(new Event('refreshCustomerData'));
            window.dispatchEvent(new Event('refreshDashboardData'));
          }, 200);
        } else {
          toast.error("Failed to update payment");
          return;
        }
      } else {
        // Create new transaction
        const response = await fetch(`${API_BASE_URL}/api/payments/customer/${tokenNo}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(paymentForm.amount),
            paymentMode: paymentForm.paymentMode,
            paymentStatus: paymentForm.paymentStatus,
            transactionDate: paymentForm.transactionDate,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment recorded successfully");
          
          // Trigger data refresh events
          setTimeout(() => {
            window.dispatchEvent(new Event('refreshCustomerData'));
            window.dispatchEvent(new Event('refreshDashboardData'));
          }, 200);
        } else {
          toast.error("Failed to record payment");
          return;
        }
      }

      setOpenDialog(false);
      setEditingTransaction(null);
      setPaymentForm({
        amount: "",
        paymentMode: "Cash",
        paymentStatus: "Completed",
        transactionDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
      fetchPaymentHistory();
      fetchCustomerData();
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setPaymentForm({
      amount: Math.abs(transaction.Amount),
      paymentMode: transaction.PaymentMode || "Cash",
      paymentStatus: transaction.PaymentStatus || "Completed",
      transactionDate: transaction.TransactionDate.split("T")[0],
      notes: transaction.Notes || "",
    });
    setOpenDialog(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMenuClick = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/payments/transaction/${transactionToDelete.TransactionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Transaction deleted successfully");
        fetchPaymentHistory();
        fetchCustomerData();
      } else {
        toast.error("Failed to delete transaction");
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error("Failed to delete transaction");
    } finally {
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    }
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      BILL_CREATED: "Bill Created",
      BILL_UPDATED: "Bill Updated",
      BILL_DELETED: "Bill Deleted",
      PAYMENT_RECEIVED: "Payment Received",
      BALANCE_ADJUSTMENT: "Balance Adjustment",
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      BILL_CREATED: "#d32f2f",
      BILL_UPDATED: "#ed6c02",
      BILL_DELETED: "#2e7d32",
      PAYMENT_RECEIVED: "#2e7d32",
      BALANCE_ADJUSTMENT: "#1976d2",
    };
    return colors[type] || "#666";
  };

  if (!customer) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 4, 
          textAlign: "center",
          bgcolor: "#0a0a0a",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography sx={{ color: "#C59A58", fontSize: "1.2rem" }}>
            Loading...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  const initials = getInitials(customer.CustomerName);
  const avatarColor = getAvatarColor(customer.CustomerName);
  const isDebit = currentBalance > 0;
  const isCredit = currentBalance < 0;

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: "1400px", 
        mx: "auto", 
        p: { xs: 1, sm: 2, md: 3 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            letterSpacing: { xs: 1, sm: 2 },
            color: "#C59A58",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.5rem" },
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0
          }}>
            CUSTOMER PAYMENTS
          </Typography>
          <Chip 
            label={`Token #${customer.TokenNo}`} 
            sx={{ 
              bgcolor: "#C59A58", 
              color: "#000", 
              fontWeight: 600,
              fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
              px: { xs: 1, sm: 1.5, md: 2 },
              py: { xs: 1.5, sm: 2, md: 2.5 },
              height: { xs: 28, sm: 36, md: 40 },
              minWidth: "fit-content"
            }} 
          />
        </Box>

        {/* BACK BUTTON */}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/customers")}
          sx={{ 
            mb: 2,
            borderColor: "#C59A58",
            color: "#C59A58",
            "&:hover": { borderColor: "#b68649", bgcolor: "rgba(197, 154, 88, 0.04)" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 0.75, sm: 1 }
          }}
        >
          Back to Customers
        </Button>

        {/* CUSTOMER INFO CARD */}
        <Paper elevation={6} sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          mb: 3, 
          borderRadius: 2, 
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          width: "100%"
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: "#C59A58",
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" }
            }}>
              Customer Information
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                sx={{
                  width: { xs: 80, sm: 90, md: 100 },
                  height: { xs: 80, sm: 90, md: 100 },
                  bgcolor: avatarColor,
                  fontSize: { xs: "1.8rem", sm: "2rem", md: "2.5rem" },
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Customer Name
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                mb: 2
              }}>
                {customer.CustomerName}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Phone Number
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                {customer.PhoneNumber}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" }, textAlign: "center" }}>
                Current Balance
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  textAlign: "center",
                  color: isDebit ? "#d32f2f" : isCredit ? "#4caf50" : "#fff",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
                }}
              >
                ₹{Math.abs(currentBalance).toLocaleString('en-IN')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: "center", 
                  mt: 0.5,
                  color: isDebit ? "#d32f2f" : isCredit ? "#4caf50" : "#fff",
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }
                }}
              >
                {isDebit ? "(Amount Due)" : isCredit ? "(Credit Balance)" : "(Settled)"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ACTION BUTTONS */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            color: "#C59A58",
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" }
          }}>
            Transaction History
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingTransaction(null);
              setPaymentForm({
                amount: "",
                paymentMode: "Cash",
                paymentStatus: "Completed",
                transactionDate: new Date().toISOString().split("T")[0],
                notes: "",
              });
              setOpenDialog(true);
            }}
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              fontWeight: 600,
              color: "#000",
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 0.6, sm: 0.8 },
              flex: { xs: 1, sm: "none" }
            }}
          >
            Add Payment
          </Button>
        </Box>

        {/* TRANSACTION HISTORY TABLE */}
        <Paper elevation={6} sx={{ 
          borderRadius: 2, 
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          p: { xs: 1, sm: 2, md: 3 }
        }}>
          <Box sx={{ overflowX: "auto", width: "100%" }}>
            <TableContainer sx={{ 
              borderRadius: 2,
              minWidth: { xs: "100%", sm: 500, md: 700 }
            }}>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0 8px",
                }}
                aria-label="transaction history table"
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#C59A58" }}>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      display: { xs: "none", sm: "table-cell" }
                    }}>
                      Bill No.
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      display: { xs: "none", md: "table-cell" }
                    }}>
                      Payment Mode
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      display: { xs: "none", lg: "table-cell" }
                    }}>
                      Notes
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      width: "60px"
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction, index) => (
                      <TableRow
                        key={transaction.TransactionId}
                        sx={{
                          bgcolor: index % 2 === 0 ? "#2a2a2a" : "#333",
                          borderRadius: 1,
                          "&:hover": { boxShadow: 1 },
                          "& td": { 
                            border: "none", 
                            verticalAlign: "middle",
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.875rem" },
                            p: { xs: 0.6, sm: 0.8, md: 1.5 }
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: "#C59A58", whiteSpace: "nowrap" }}>
                          {new Date(transaction.TransactionDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={getTransactionTypeLabel(transaction.TransactionType)}
                            size="small"
                            sx={{
                              bgcolor: getTransactionTypeColor(transaction.TransactionType),
                              color: "white",
                              fontWeight: 600,
                              fontSize: { xs: "0.6rem", sm: "0.7rem" }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff", display: { xs: "none", sm: "table-cell" }, whiteSpace: "nowrap" }}>
                          {transaction.BillNumber || "-"}
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600,
                          color: transaction.Amount > 0 ? "#d32f2f" : "#4caf50",
                          whiteSpace: "nowrap"
                        }}>
                          {transaction.Amount > 0 ? "+" : ""}₹{Math.abs(transaction.Amount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", display: { xs: "none", md: "table-cell" }, whiteSpace: "nowrap" }}>
                          {transaction.TransactionType === "PAYMENT_RECEIVED" ? (transaction.PaymentMode || "-") : "-"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {transaction.TransactionType === "PAYMENT_RECEIVED" && transaction.PaymentStatus ? (
                            <Chip
                              label={transaction.PaymentStatus}
                              size="small"
                              sx={{
                                bgcolor: transaction.PaymentStatus === "Completed" ? "#2e7d32" : 
                                         transaction.PaymentStatus === "Pending" ? "#ed6c02" : "#1976d2",
                                color: "white",
                                fontWeight: 600,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" }
                              }}
                            />
                          ) : (
                            <Typography sx={{ color: "#888", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ 
                          maxWidth: 150, 
                          overflow: "hidden", 
                          textOverflow: "ellipsis", 
                          color: "#fff",
                          display: { xs: "none", lg: "table-cell" },
                          whiteSpace: "nowrap"
                        }}>
                          {transaction.Notes || "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: "60px" }}>
                          {transaction.TransactionType === "PAYMENT_RECEIVED" ? (
                            <IconButton
                              onClick={(e) => handleMenuClick(e, transaction)}
                              sx={{ 
                                color: "#C59A58",
                                p: { xs: 0.5, sm: 0.8 }
                              }}
                              size="small"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography sx={{ color: "#888", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2, border: "none" }}>
                        <Typography variant="body1" sx={{ 
                          color: "#888",
                          fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" }
                        }}>
                          No transaction history yet. Record your first payment!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Box>

      {/* ADD/EDIT PAYMENT DIALOG */}
      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          setEditingTransaction(null);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2,
            mx: { xs: 1, sm: 0 }
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58", fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, p: { xs: 1.5, sm: 2 } }}>
          {editingTransaction ? "Edit Payment Transaction" : "Add Payment Transaction"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Mode"
                  value={paymentForm.paymentMode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                  fullWidth
                  slotProps={{ 
                    select: { 
                      native: true,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Other">Other</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Status"
                  value={paymentForm.paymentStatus}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentStatus: e.target.value })}
                  fullWidth
                  slotProps={{ 
                    select: { 
                      native: true,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                >
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Transaction Date"
                  type="date"
                  value={paymentForm.transactionDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionDate: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this payment..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              setEditingTransaction(null);
            }}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            variant="contained" 
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              color: "#000",
              fontWeight: 600
            }}
          >
            {editingTransaction ? "Update Payment" : "Add Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#fff" }}>
            Are you sure you want to delete this payment transaction? This will recalculate the customer balance.
          </Typography>
          {transactionToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#2a2a2a", borderRadius: 1, border: "1px solid #444" }}>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                <strong style={{ color: "#C59A58" }}>Amount:</strong> ₹{Math.abs(transactionToDelete.Amount).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                <strong style={{ color: "#C59A58" }}>Date:</strong> {new Date(transactionToDelete.TransactionDate).toLocaleDateString()}
              </Typography>
              {transactionToDelete.Notes && (
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  <strong style={{ color: "#C59A58" }}>Notes:</strong> {transactionToDelete.Notes}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: 1,
            minWidth: 120
          }
        }}
      >
        <MenuItem 
          onClick={() => handleEditTransaction(selectedTransaction)}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: "1rem", color: "#C59A58" }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => handleDeleteClick(selectedTransaction)}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: "1rem", color: "#f44336" }} />
          Delete
        </MenuItem>
      </Menu>
    </AppLayout>
  );
}
